import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DatabaseService } from '../../services/database/database.service';
import { Router } from '@angular/router';
import { ToastService } from '../../services/toast/toast.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-final-data-download-modal-component',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './final-data-download-modal-component.component.html',
  styleUrl: './final-data-download-modal-component.component.scss'
})
export class FinalDataDownloadModalComponentComponent implements OnInit {

  downloadLink: string = '';
  executionTime: string = '';
  rowsGenerated: number | null = null;
  scriptName: string = '';
  scriptContent: string = '';
  successOutput: string = '';
  
  // Adding structured DMSH properties
  dmshOutput: string = '';
  dmshStatus: string = '';
  dmshSuccessfulTables: number = 0;
  dmshFailedTables: number = 0;
  dmshResponses: any[] = [];
  dmshErrors: any[] = [];
  hasDmshData: boolean = false;
  
  // Add this missing property
  scriptOutput: string = '';
  isOutputExpanded: boolean = false;
  outputMaxLength: number = 500; // Characters to show initially
  
  // Download ID for direct file retrieval
  downloadId: string = '';
  
  // New properties for download info
  isDownloadAvailable: boolean = false;
  downloadFileName: string = '';
  downloadFileSize: number = 0;
  isDownloading: boolean = false;
  
  // Script execution parameters
  scriptEdited: boolean = false;
  scriptAutofix: boolean = false;

  constructor(
    private router: Router,
    private activeModal: NgbActiveModal,
    private databaseService: DatabaseService,
    private toastService: ToastService,
  ){}

  ngOnInit(): void { }
  
  // Method to initialize the component with full execution response
  setExecutionResponse(response: any): void {
    if (!response) return;
    
    this.executionTime = response.execution_time || '';
    this.rowsGenerated = response.rows_generated || null;
    this.successOutput = response.output || '';
    
    // Process DMSH data if available
    if (response.dmsh) {
      this.hasDmshData = true;
      
      // Store the raw dmsh output for compatibility
      this.dmshOutput = typeof response.dmsh === 'string' ? response.dmsh : JSON.stringify(response.dmsh);
      
      // Parse the structured DMSH data
      if (typeof response.dmsh === 'object') {
        this.dmshStatus = response.dmsh.status || 'unknown';
        this.dmshSuccessfulTables = response.dmsh.successful_tables || 0;
        this.dmshFailedTables = response.dmsh.failed_tables || 0;
        this.dmshResponses = response.dmsh.responses || [];
        this.dmshErrors = response.dmsh.errors || [];
      }
    } else {
      this.hasDmshData = false;
      this.dmshOutput = 'Not available';
    }
    
    // Store script output if available
    if (response.output) {
      this.scriptOutput = response.output;
    }
    
    // Check if data_download is available
    if (response.data_download && response.data_download.available) {
      this.isDownloadAvailable = true;
      this.downloadFileName = response.data_download.file_name || 'data_files.zip';
      this.downloadFileSize = response.data_download.file_size || 0;
      
      // Get the download ID directly if provided
    if (response.data_download.download_id) {
      this.downloadId = response.data_download.download_id;
    }
    // Fall back to extracting from file_path if needed
    else if (response.data_download.file_path) {
      try {
        const pathParts = response.data_download.file_path.split(/[\/\\]/); // Handle both forward and backslashes
        this.downloadId = pathParts[pathParts.length - 1];
        console.log("Extracted download ID from path:", this.downloadId);
      } catch (error) {
        console.error("Error extracting download ID from path:", error);
      }
    }
      
      // Store download link if provided
      if (response.data_download.download_url) {
        this.downloadLink = response.data_download.download_url;
      }

      // Log for debugging
      console.log("Download data available:", {
        available: this.isDownloadAvailable,
        fileName: this.downloadFileName,
        fileSize: this.downloadFileSize,
        downloadId: this.downloadId
      });

    } else {
      this.isDownloadAvailable = false;
    }
  }
  
  // Toggle output expansion
  toggleOutputExpansion(): void {
    this.isOutputExpanded = !this.isOutputExpanded;
  }
  
  // Get a truncated version of the output if needed
  get truncatedOutput(): string {
    if (!this.scriptOutput || this.scriptOutput.length <= this.outputMaxLength || this.isOutputExpanded) {
      return this.scriptOutput;
    }
    return this.scriptOutput.substring(0, this.outputMaxLength) + '...';
  }
  
  // Check if the output needs truncation
  get isOutputTruncated(): boolean {
    return !this.isOutputExpanded && 
           typeof this.scriptOutput === 'string' && 
           this.scriptOutput.length > this.outputMaxLength;
  }

  onSaveAndReturn(){
    this.databaseService.saveWorkflow(this.scriptName).subscribe({
      next: (response) => {
        this.activeModal.close();
        this.router.navigate(['/workflowcomp']);
      },
      error: (error) => {
        console.error('Failed to save workflow:', error);
        this.activeModal.close();
        this.toastService.showToast(`Error Saving Workflow : ${error}`, { classname: "bg-danger text-light" })
      }
    });
  }

  onCancel() {
    this.activeModal.dismiss();
  }

  downloadFile() {
    if (!this.isDownloadAvailable) {
      this.toastService.showToast('Download not available for this execution', { classname: "bg-warning text-light" });
      return;
    }
    
    // Use the filename as the download ID
    const downloadIdToUse = this.downloadFileName;
    
    console.log("Using download ID:", downloadIdToUse);
    
    if (!downloadIdToUse) {
      console.error("Cannot determine download ID:", this);
      this.toastService.showToast('Download information is incomplete', { classname: "bg-danger text-light" });
      return;
    }
    
    this.isDownloading = true;
    
    // Use the dedicated download endpoint
    this.databaseService.downloadGeneratedFile(downloadIdToUse).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.downloadFileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isDownloading = false;
        this.toastService.showToast('Download completed successfully', { classname: "bg-success text-light" });
      },
      error: error => {
        console.error('Download failed:', error);
        this.isDownloading = false;
        this.toastService.showToast('Download failed. Please try again.', { classname: "bg-danger text-light" });
      }
    });
  }
}